'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { generateAvatarUrl } from '@/utils/helpers/avatarHelper';

type UserAvatarProps = {
    avatarUrl?: string;
    schoomyers_id?: number;
    size: number;
    breakpoints?: {
        md?: number;
        lg?: number;
        xl?: number;
    };
    className?: string;
    borderColorClassName: string;
    onClick?: () => void;
};

export default function UserAvatar({
    avatarUrl,
    schoomyers_id,
    size,
    breakpoints = {},
    className = '',
    borderColorClassName = '',
    onClick,
}: UserAvatarProps) {
    const [currentSize, setCurrentSize] = useState<number>(size);
    const [bgImage, setBgImage] = useState<string | undefined>(undefined);

    useEffect(() => {
        const updateSize = () => {
            const width = window.innerWidth;
            if (width >= 1280 && breakpoints.xl) {
                setCurrentSize(breakpoints.xl);
            } else if (width >= 1024 && breakpoints.lg) {
                setCurrentSize(breakpoints.lg);
            } else if (width >= 768 && breakpoints.md) {
                setCurrentSize(breakpoints.md);
            } else {
                setCurrentSize(size);
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, [size, breakpoints]);

    useEffect(() => {
        if (avatarUrl) {
            setBgImage(`url(${avatarUrl})`);
        } else if (schoomyers_id !== undefined) {
            setBgImage(generateAvatarUrl(schoomyers_id));
        }
    }, [avatarUrl, schoomyers_id]);

    const borderWidth = Math.max(4, Math.round(currentSize / 18));
    const gapWidth = Math.max(3, Math.round(currentSize / 30));

    return (
        <div
            className={`relative flex-shrink-0 rounded-full ${className}`}
            onClick={onClick}
            style={{
                width: `${currentSize}px`,
                height: `${currentSize}px`,
                padding: `${gapWidth}px`,
                borderWidth: '2px',
                borderStyle: 'solid',
                borderColor: 'transparent',
            }}
        >
            <motion.div
                className="w-full h-full rounded-full bg-color-white-base"
                style={{
                    backgroundImage: bgImage,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
            />

            <motion.div
                className={`absolute pointer-events-none rounded-full border ${borderColorClassName}`}
                style={{
                    top: `-${borderWidth}px`,
                    left: `-${borderWidth}px`,
                    width: `calc(100% + ${borderWidth * 2}px)`,
                    height: `calc(100% + ${borderWidth * 2}px)`,
                    borderWidth: `${borderWidth}px`,
                    borderStyle: 'solid',
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.5 }}
            />
        </div>
    );
}
